import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import BusinessEmailAddressInput from '../form-inputs/business-email-address-input/BusinessEmailAddressInput';
import BusinessPhoneNumberInput from '../form-inputs/business-phone-number-input/BusinessPhoneNumberInput';
import BrushIcon from '../../../assets/svg/brush.svg?react';

import CircularProgress from '@mui/material/CircularProgress';
import { useEmploymentCardForm } from './useEmploymentCardForm';
import styles from './EmploymentCard.module.scss';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import Tooltip from '@mui/material/Tooltip';
// import { SocialLink } from '../social-media-form/SocialMediaForm';
import { Employee } from '../../models/employee.model';
import { BadgeOutlined } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { USER_PERMISSIONS } from '../constants/permissions';
import { useCheckPermission } from '../hooks/useCheckPermission';

type EmploymentCardProps = {
  simplified?: boolean;
  employee: Partial<Employee>;
};

export default function EmploymentCard({ simplified, employee }: EmploymentCardProps) {
  const { t } = useTranslation();
  const isLoading = false;
  const {
    formData,
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    confirmExit,
    cancelChanges,
  } = useEmploymentCardForm(employee);

  const checkPermission = useCheckPermission();

  return (
    <Grid container className='inputs-editable'>
      <Grid size={{ xs: 12 }}>
        <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
          <CardContent className={isLoading ? styles.card_content_min_height : ''}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Grid
                  container
                  alignItems='center'
                  paddingTop='0 !important'
                  paddingLeft='0 !important'
                  paddingRight='0 !important'
                  paddingBottom='16px !important'
                  minHeight='60px'
                  flexWrap='nowrap'
                  p={1}
                >
                  <Grid size={{ xs: 11 }} container alignItems='center' gap={1}>
                    <Grid sx={{ marginBottom: '0.2rem' }}>
                      <BadgeOutlined />
                    </Grid>
                    <Grid>
                      <Typography variant='h6'>{t('settings.titles.employment')}</Typography>
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 1 }} container justifyContent='flex-end'>
                    {!isEdited ? (
                      <Tooltip
                        className='flex-shrink-0'
                        title={
                          checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE)
                            ? t('buttons.edit')
                            : t('messages.contactAdministrator')
                        }
                        placement='top'
                      >
                        <IconButton
                          onClick={toggleEditing}
                          disabled={!checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE)}
                        >
                          <BrushIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box display='flex' gap='8px' alignItems='center'>
                        <Button
                          type='button'
                          className='btn-default btn-action-basic btn-cancel'
                          role='button'
                          variant='contained'
                          size='large'
                          onClick={cancelChanges}
                        >
                          {t('buttons.cancel')}
                        </Button>
                        <Button
                          type='submit'
                          className='btn-default btn-action-basic'
                          role='button'
                          disabled={!isEdited}
                          variant='contained'
                          size='large'
                          onClick={saveChanges}
                        >
                          {t('buttons.save')}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                {!simplified ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          {' '}
                          <TextField
                            fullWidth
                            value={formData.employee_identification_number ?? ''}
                            label={t('labels.employeeId')}
                            onChange={(e) => {
                              handleChange('employee_identification_number')(e.target.value);
                            }}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <BusinessPhoneNumberInput
                            value={formData.employee_tax_number}
                            onChange={handleChange('employee_tax_number')}
                            label={t('labels.employeeTaxNumber')}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          {' '}
                          <BusinessEmailAddressInput
                            value={formData.position_title}
                            onChange={handleChange('position_title')}
                            disabled={!isEdited}
                            label={t('labels.positionTitle')}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          {' '}
                          <TextField
                            fullWidth
                            value={formData.department ?? ''}
                            label={t('labels.department')}
                            onChange={(e) => {
                              handleChange('department')(e.target.value);
                            }}
                            disabled={!isEdited}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                              {' '}
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label={t('labels.startDate')}
                                  value={formData.start_date ? dayjs(formData.start_date) : null}
                                  onChange={(newValue) => {
                                    const formattedDate = newValue
                                      ? newValue.format('YYYY-MM-DD')
                                      : null;
                                    handleChange('start_date')(formattedDate);
                                  }}
                                  disabled={!isEdited}
                                  format='DD/MM/YYYY'
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      placeholder: 'DD/MM/YYYY',
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              {' '}
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label={t('labels.endDate')}
                                  value={formData.end_date ? dayjs(formData.end_date) : null}
                                  onChange={(newValue) => {
                                    const formattedDate = newValue
                                      ? newValue.format('YYYY-MM-DD')
                                      : null;
                                    handleChange('end_date')(formattedDate);
                                  }}
                                  disabled={!isEdited}
                                  format='DD/MM/YYYY'
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      placeholder: 'DD/MM/YYYY',
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        value={formData.position_description ?? ''}
                        label={t('labels.positionDescription')}
                        onChange={(e) => {
                          handleChange('position_description')(e.target.value);
                        }}
                        disabled={!isEdited}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          {' '}
                          <BusinessEmailAddressInput
                            value={formData.position_title}
                            onChange={handleChange('position_title')}
                            disabled={!isEdited}
                            label={t('labels.positionTitle')}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          {' '}
                          <TextField
                            fullWidth
                            value={formData.department ?? ''}
                            label={t('labels.department')}
                            onChange={(e) => {
                              handleChange('department')(e.target.value);
                            }}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          {' '}
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label={t('labels.startDate')}
                              value={formData.start_date ? dayjs(formData.start_date) : null}
                              onChange={(newValue) => {
                                const formattedDate = newValue
                                  ? newValue.format('YYYY-MM-DD')
                                  : null;
                                handleChange('start_date')(formattedDate);
                              }}
                              disabled={!isEdited}
                              format='DD/MM/YYYY'
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  placeholder: 'DD/MM/YYYY',
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>

                        <Grid size={{ xs: 6 }}>
                          {' '}
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label={t('labels.endDate')}
                              value={formData.end_date ? dayjs(formData.end_date) : null}
                              onChange={(newValue) => {
                                const formattedDate = newValue
                                  ? newValue.format('YYYY-MM-DD')
                                  : null;
                                handleChange('end_date')(formattedDate);
                              }}
                              disabled={!isEdited}
                              format='DD/MM/YYYY'
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  placeholder: 'DD/MM/YYYY',
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            value={formData.position_description ?? ''}
                            label={t('labels.positionDescription')}
                            onChange={(e) => {
                              handleChange('position_description')(e.target.value);
                            }}
                            disabled={!isEdited}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {
                  <Grid container className={styles.container_margin_top} alignItems='center'>
                    <Grid>
                      {/* <Tooltip title={t('businessProfile.addSocials')} placement='top'>
                        <IconButton
                          onClick={toogleEditingSocialModal}
                          className={styles.add_social_button}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip> */}
                    </Grid>
                  </Grid>
                }
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
      {/* <AddSocialsModal
        open={showAddSocialModal}
        onClose={saveChangesSocialForm}
        onConfirm={toogleEditingSocialModal}
      /> */}
    </Grid>
  );
}
